import { Fragment, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DevHubAlert,
  DevHubBox,
  DevHubCard,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';
import { slugifyHyphen } from '../../util/stringFormat';

/**
 * A component to render an API Portal category.
 * @constructor
 */
const ApiPortalCategory = () => {
  const navigate = useNavigate();
  const { categoryName } = useParams<{ categoryName: string }>();
  const { org, loading, error, formattedOrgName } =
    useContext(ApiPortalContext);

  const detailCards = [
    {
      title: 'Scenarios',
      description: `Resources, guides, tutorials, and sample apps for common use-case scenarios to get started with ${formattedOrgName} APIs.`,
      onClick: () =>
        navigate(
          `/api-portals/${org._normalizedName}/${categoryName}/scenarios`,
        ),
    },
    {
      title: 'API Specs',
      description: `Find the OpenAPI specifications for all the ${formattedOrgName} API services available for ${categoryName
        ?.replace(/-/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())}.`,
      onClick: () =>
        navigate(`/api-portals/${org._normalizedName}/${categoryName}/specs`),
    },
    {
      title: 'Events',
      description: `Find details to get started with Event and Messaging capability.`,
      onClick: () =>
        navigate(`/api-portals/${org._normalizedName}/${categoryName}/events`),
    },
  ];

  const renderDetailCards = () => {
    const category = org.categoriesPaged?.categories?.find(
      x => slugifyHyphen(x.name) === categoryName,
    );
    const hasScenarios =
      (category?.apiScenariosPaged?.scenarios?.length ?? 0) > 0;
    const hasSpecs = (category?.apiSpecsPaged?.specs?.length ?? 0) > 0;
    const hasEvents = (category?.apiEventsPaged?.events?.length ?? 0) > 0;

    return (
      <>
        <DevHubBox display="flex">
          <DevHubGrid container spacing={2}>
            {detailCards.map(card => {
              const isLinkable =
                (card.title === 'Scenarios' && hasScenarios) ||
                (card.title === 'API Specs' && hasSpecs) ||
                (card.title === 'Events' && hasEvents);

              return (
                <DevHubGrid item xs={12} md={6} lg={4} key={card.title}>
                  <DevHubCard
                    onClick={isLinkable ? card.onClick : undefined}
                    density="compact"
                    consistentHeight
                    topBorder="custom"
                    topBorderColor={isLinkable ? 'blue' : 'medGray'}
                    bodyPosition="middle"
                    subTitle=""
                    body={
                      <Fragment>
                        <DevHubBox
                          tbPadding="large"
                          display="flex"
                          justifyContent="center"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <DevHubDynamicIcon
                            color="action"
                            fontSize="large"
                            iconName="LibraryBooks"
                          />
                          <DevHubTypography variant="h5">
                            {card.title}
                          </DevHubTypography>
                          <DevHubTypography>
                            {card.description}
                          </DevHubTypography>
                        </DevHubBox>
                      </Fragment>
                    }
                  />
                </DevHubGrid>
              );
            })}
          </DevHubGrid>
        </DevHubBox>
      </>
    );
  };

  const handleBackClick = () => {
    navigate(`/api-portals/${org._normalizedName}`);
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal organization data: {error.message}
      </DevHubAlert>
    );
  }

  return (
    <ApiPortalTabbedPage
      formattedOrgName={formattedOrgName}
      apiManagementContent={renderDetailCards()}
      handleBackClick={handleBackClick}
    />
  );
};

export default ApiPortalCategory;
